import { z } from "zod";

import { isoDateTimeSchema } from "@/src/models/common-schema";
import { ReferralStatus } from "@/src/models/documents/referral";

export enum PatientEngagementType {
  SMS = "SMS",
  Phone = "Phone",
  Email = "Email",
  Online = "Online",
  InPerson = "In-person",
}

export enum PatientEngagementDirection {
  Inbound = "Inbound",
  Outbound = "Outbound",
}

export enum PatientEngagementWorkflow {
  SCHEDULING = "scheduling",
  OTHER = "other",
}

export const PATIENT_ENGAGEMENT_WORKFLOW_LABELS: Record<PatientEngagementWorkflow, string> = {
  [PatientEngagementWorkflow.SCHEDULING]: "Scheduling",
  [PatientEngagementWorkflow.OTHER]: "Other",
};

export enum PatientEngagementOutcome {
  COMPLETED = "completed",
  DECLINED = "declined",
  FOLLOW_UP_REQUESTED = "follow_up_requested",
  NOT_REACHED = "not_reached",
  ABANDONED = "abandoned",
  ESCALATED = "escalated",
}

export const PATIENT_ENGAGEMENT_OUTCOME_LABELS: Record<PatientEngagementOutcome, string> = {
  [PatientEngagementOutcome.COMPLETED]: "Completed",
  [PatientEngagementOutcome.DECLINED]: "Declined",
  [PatientEngagementOutcome.FOLLOW_UP_REQUESTED]: "Follow-up Requested",
  [PatientEngagementOutcome.NOT_REACHED]: "Not Reached",
  [PatientEngagementOutcome.ABANDONED]: "Abandoned",
  [PatientEngagementOutcome.ESCALATED]: "Escalated",
};

// Reason options gated on (workflow, outcome). Today only Scheduling + Declined has options;
// every other combination has no reasons and the outcome button is rendered as a plain button.
export enum PatientEngagementOutcomeReason {
  NO_SYMPTOMS = "no_symptoms",
  COST = "cost",
  TRANSPORTATION = "transportation",
  TIME_AVAILABILITY = "time_availability",
  DIFFERENT_PROVIDER = "different_provider",
  BAD_EXPERIENCE = "bad_experience",
  FAMILY_ISSUE = "family_issue",
  OTHER = "other",
  // Escalation reasons — mirror TransferPrimaryReason from transcript-evaluation
  // so voice ingestion can map straight from the transcript classifier output.
  APPOINTMENT_NOT_FOUND = "appointment_not_found",
  INELIGIBLE = "ineligible",
  OUT_OF_SCOPE_INTENT = "out_of_scope_intent",
  PATIENT_NOT_FOUND = "patient_not_found",
  PATIENT_REQUEST = "patient_request",
  TOOL_ERROR = "tool_error",
  URGENT_CALL = "urgent_call",
  UNKNOWN = "unknown",
}

export const PATIENT_ENGAGEMENT_OUTCOME_REASON_LABELS: Record<PatientEngagementOutcomeReason, string> = {
  [PatientEngagementOutcomeReason.NO_SYMPTOMS]: "No Symptoms",
  [PatientEngagementOutcomeReason.COST]: "Cost",
  [PatientEngagementOutcomeReason.TRANSPORTATION]: "Transportation",
  [PatientEngagementOutcomeReason.TIME_AVAILABILITY]: "Time Availability",
  [PatientEngagementOutcomeReason.DIFFERENT_PROVIDER]: "Different Provider",
  [PatientEngagementOutcomeReason.BAD_EXPERIENCE]: "Bad Experience",
  [PatientEngagementOutcomeReason.FAMILY_ISSUE]: "Family Issue",
  [PatientEngagementOutcomeReason.OTHER]: "Other",
  [PatientEngagementOutcomeReason.APPOINTMENT_NOT_FOUND]: "Appointment Not Found",
  [PatientEngagementOutcomeReason.INELIGIBLE]: "Ineligible",
  [PatientEngagementOutcomeReason.OUT_OF_SCOPE_INTENT]: "Out of Scope",
  [PatientEngagementOutcomeReason.PATIENT_NOT_FOUND]: "Patient Not Found",
  [PatientEngagementOutcomeReason.PATIENT_REQUEST]: "Patient Requested Human",
  [PatientEngagementOutcomeReason.TOOL_ERROR]: "System Error",
  [PatientEngagementOutcomeReason.URGENT_CALL]: "Urgent",
  [PatientEngagementOutcomeReason.UNKNOWN]: "Unknown",
};

export type PatientEngagementOutcomeReasonOption = {
  value: PatientEngagementOutcomeReason;
  label: string;
};

function toReasonOptions(values: PatientEngagementOutcomeReason[]): PatientEngagementOutcomeReasonOption[] {
  return values.map((value) => ({ value, label: PATIENT_ENGAGEMENT_OUTCOME_REASON_LABELS[value] }));
}

const SCHEDULING_DECLINED_OUTCOME_REASON_OPTIONS: PatientEngagementOutcomeReasonOption[] = toReasonOptions([
  PatientEngagementOutcomeReason.NO_SYMPTOMS,
  PatientEngagementOutcomeReason.COST,
  PatientEngagementOutcomeReason.TRANSPORTATION,
  PatientEngagementOutcomeReason.TIME_AVAILABILITY,
  PatientEngagementOutcomeReason.DIFFERENT_PROVIDER,
  PatientEngagementOutcomeReason.BAD_EXPERIENCE,
  PatientEngagementOutcomeReason.FAMILY_ISSUE,
  PatientEngagementOutcomeReason.OTHER,
]);

const ESCALATED_OUTCOME_REASON_OPTIONS: PatientEngagementOutcomeReasonOption[] = toReasonOptions([
  PatientEngagementOutcomeReason.APPOINTMENT_NOT_FOUND,
  PatientEngagementOutcomeReason.INELIGIBLE,
  PatientEngagementOutcomeReason.OUT_OF_SCOPE_INTENT,
  PatientEngagementOutcomeReason.PATIENT_NOT_FOUND,
  PatientEngagementOutcomeReason.PATIENT_REQUEST,
  PatientEngagementOutcomeReason.TOOL_ERROR,
  PatientEngagementOutcomeReason.URGENT_CALL,
  PatientEngagementOutcomeReason.UNKNOWN,
]);

export function getOutcomeReasonOptions(
  workflow: PatientEngagementWorkflow | null | undefined,
  outcome: PatientEngagementOutcome | null | undefined
): PatientEngagementOutcomeReasonOption[] {
  if (workflow === PatientEngagementWorkflow.SCHEDULING && outcome === PatientEngagementOutcome.DECLINED) {
    return SCHEDULING_DECLINED_OUTCOME_REASON_OPTIONS;
  }

  if (workflow === PatientEngagementWorkflow.SCHEDULING && outcome === PatientEngagementOutcome.ESCALATED) {
    return ESCALATED_OUTCOME_REASON_OPTIONS;
  }

  return [];
}

export const patientEngagementSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string(),
  groupId: z.string(),
  type: z.nativeEnum(PatientEngagementType),
  direction: z.nativeEnum(PatientEngagementDirection).nullable(),
  timeEngaged: isoDateTimeSchema,
  userId: z.string(),
  referralId: z.string().nullable(),
  workflow: z.nativeEnum(PatientEngagementWorkflow).nullable(),
  outcome: z.nativeEnum(PatientEngagementOutcome).nullable(),
  outcomeReason: z.nativeEnum(PatientEngagementOutcomeReason).nullable(),
  note: z.string().nullable(),
  transcriptId: z.string().uuid().nullable(),
  autoSummary: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  deletedAt: isoDateTimeSchema.nullable(),
});

export type PatientEngagement = z.infer<typeof patientEngagementSchema>;

export const patientEngagementShape: Partial<PatientEngagement> = {
  id: "",
  patientId: "",
  groupId: "",
  type: PatientEngagementType.SMS,
  direction: null,
  timeEngaged: "",
  userId: "",
  referralId: null,
  workflow: null,
  outcome: null,
  outcomeReason: null,
  note: null,
  transcriptId: null,
  autoSummary: null,
  createdAt: "",
  deletedAt: null,
};

export const createPatientEngagementSchema = z
  .object({
    patientId: z.string(),
    type: z.nativeEnum(PatientEngagementType),
    direction: z.nativeEnum(PatientEngagementDirection).nullable().optional(),
    timeEngaged: isoDateTimeSchema,
    referralId: z.string().nullable().optional(),
    workflow: z.nativeEnum(PatientEngagementWorkflow).nullable().optional(),
    outcome: z.nativeEnum(PatientEngagementOutcome).nullable().optional(),
    outcomeReason: z.nativeEnum(PatientEngagementOutcomeReason).nullable().optional(),
    note: z.string().nullable().optional(),
    // Referral status to apply alongside the engagement (from the Add Engagement dialog's dropdown).
    referralStatus: z.nativeEnum(ReferralStatus).nullish(),
    // Human-reviewed note to fax the referring provider on a close-out (from the confirm dialog).
    // Distinct from `note`: the close-out fax is sent only when this is present, never from the raw note.
    reviewedCloseOutNote: z.string().nullish(),
    // Appointment the user confirmed to fax when this engagement books the referral; null = auto-select.
    reviewedAppointmentId: z.string().nullish(),
    // Allows a confirmed status transition to proceed without enqueueing its referral-update fax.
    skipReferralUpdateFax: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.outcomeReason) return;

    const allowed = getOutcomeReasonOptions(data.workflow ?? null, data.outcome ?? null);

    if (allowed.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["outcomeReason"],
        message: "outcomeReason is not supported for this workflow/outcome combination",
      });

      return;
    }

    if (!allowed.some((o) => o.value === data.outcomeReason)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["outcomeReason"],
        message: `outcomeReason ${data.outcomeReason} is not valid for workflow=${data.workflow}, outcome=${data.outcome}`,
      });
    }
  });

export type CreatePatientEngagement = z.infer<typeof createPatientEngagementSchema>;

/**
 * Resolves the referral status an engagement applies: an explicit dropdown pick wins, then a DECLINED
 * outcome closes the referral, then an outbound contact on a not-started referral moves it to outreach.
 * Shared by the create-engagement route and the client so both agree on when a close-out is happening.
 */
export function resolveEngagementReferralStatus(
  engagement: {
    referralStatus?: ReferralStatus | null;
    outcome?: PatientEngagementOutcome | null;
    direction?: PatientEngagementDirection | null;
  },
  previousStatus: ReferralStatus | null
): ReferralStatus | null {
  if (engagement.referralStatus) return engagement.referralStatus;
  if (engagement.outcome === PatientEngagementOutcome.DECLINED) return ReferralStatus.CANCELLED;
  if (engagement.direction === PatientEngagementDirection.Outbound && previousStatus === ReferralStatus.NOT_STARTED) {
    return ReferralStatus.IN_OUTREACH;
  }

  return null;
}
